import { Injectable } from '@angular/core';
import { PAGES_CONFIG } from '../../../config/pages.config';
import { LABELS } from '../../../config/labels.config';
import { HttpClient } from '@angular/common/http';
import { DatabaseService } from '../database/database.service';
import { AlertService } from '../alert/alert.service';
import { NGXLogger } from 'ngx-logger';
import { RestNotifService } from '../rest-notif/rest-notif.service';
import { PATHS_CONFIG } from '../../../config/paths.config';
import { Router } from '@angular/router';
import { Platform, ToastController } from '@ionic/angular';
import { FirebaseMessaging } from '@awesome-cordova-plugins/firebase-messaging/ngx';
import { getMessaging, Messaging, getToken } from "firebase/messaging";

declare var cordova: any;

@Injectable({
  providedIn: 'root'
})
export class MessagingService {

  messaging: Messaging | null = null;

  constructor(
    public http: HttpClient,
    public databaseService: DatabaseService,
    public alertService: AlertService,
    private logger: NGXLogger,
    public notifRestService: RestNotifService,
    private router: Router,
    private platform: Platform,
    private toastController: ToastController,
    private firebaseMessaging: FirebaseMessaging
  ) {
    this.logger.debug('Hello MessagingService Service');
    this.initFirebaseMessaging();
  }

  async getPermission(): Promise<string | null> {
    this.logger.debug("Call to getPermission()");
    let token: string | null = null;

    try {
      if (this.platform.is("cordova")) {
        if (cordova.plugins && cordova.plugins.firebase) {
          this.logger.debug("Using Cordova Firebase plugin for push notifications");

          if (this.platform.is("ios")) {
            const hasPermission = await cordova.plugins.firebase.messaging.hasPermission();
            if (!hasPermission) {
              await cordova.plugins.firebase.messaging.requestPermission();
            }
          }

          token = await cordova.plugins.firebase.messaging.getToken();
        } else {
          throw new Error("Cordova Firebase plugin not available.");
        }
      } else {
        if (Notification.permission !== "granted") {
          await Notification.requestPermission();
          if (Notification.permission === "denied") {
            throw new Error("User denied notification permission.");
          }
        }

        const messaging = this.getMessaging();
        if (messaging) {
          token = await getToken(messaging);
        }
      }

      this.logger.debug("Push notification token:", token);
      return token;
    } catch (err) {
      this.logger.error("Error retrieving push token:", err);
      return null;
    }
  }

  private getMessaging(): Messaging | null {
    if (!this.messaging) {
      try {
        this.messaging = getMessaging();
        this.logger.debug('Firebase Messaging initialized successfully');
      } catch (err) {
        this.logger.error('Error initializing Firebase Messaging:', err);
      }
    }
    return this.messaging;
  }

  private initFirebaseMessaging() {
    if (this.platform.is('cordova')) {
      this.firebaseMessaging.requestPermission().then(() => {
        this.logger.debug('Push notification permission granted.');
        this.getToken();
      }).catch((err) => {
        this.logger.error('Push notification permission denied.', err);
      });

      this.firebaseMessaging.onMessage().subscribe((msg) => {
        this.logger.debug('Foreground message received:', msg);
        this.presentNotifToast(msg.notification.title, msg.notification.body, 'notifications', () => {
          this.router.navigateByUrl(msg.data?.link || '/');
        });
      });

      this.firebaseMessaging.onBackgroundMessage().subscribe((msg) => {
        this.logger.debug('Background message received:', msg);
        this.presentNotifToast(msg.notification.title, msg.notification.body, 'notifications', () => {
          this.router.navigateByUrl(msg.data?.link || '/');
        });
      });
    }
  }

  private async getToken() {
    this.firebaseMessaging.getToken().then((token) => {
      if (token) {
        this.logger.debug('FCM Token:', token);
        this.saveToken(token);
      } else {
        this.logger.log('No FCM token available.');
      }
    }).catch((err) => {
      this.logger.error('Error retrieving FCM token:', err);
    });
  }

  async saveToken(token: string) {
    if (token) {
      await this.databaseService.initUserActivity(token);
    }
  }

  async presentNotifToast(header: string, message: string, icon: string, callback: () => void) {
    const toast = await this.toastController.create({
      header,
      message,
      position: 'top',
      duration: 3000,
      buttons: [
        { side: 'start', icon },
        { text: 'Consulter', handler: callback }
      ]
    });

    await toast.present();
    toast.onDidDismiss().then(() => this.logger.debug('Notification toast dismissed'));
  }
}
